<template>
  <div class="row align-items-center d-flex">
    <div class="col-12 col-md-6 document-input-field">
      <TextInput :required="true" id="register-document" :label="foreignDocument ? 'Documento' : 'CPF'" :maxLength="30" :model="documentModel"
        type="text" :placeholder="foreignDocument ? 'Informe o número do documento' : 'Informe o CPF'"
        :errorMsg="errorMessage" :mask="foreignDocument ? FOREIGN_DOCUMENT_MASK : CPF_MASK" />
    </div>
    <div class="col-12 col-md-6 document-input-switch">
      <div class="form-check form-switch mt-1 mb-2">
        <input class="form-check-input" v-model="foreignDocument" type="checkbox" id="register-foreigner-switch">
        <label class="form-label text-muted small mb-0" for="register-foreigner-switch">Resido fora do
          Brasil</label>
      </div>
    </div>
    <span v-if="!!error" id="error-msg-container"></span>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { CPF_MASK, documentError, documentValue, formatCpf, FOREIGN_DOCUMENT_MASK } from "@/schemas/document";
import { useSessionStore } from "@/store/useSessionStore";
import type { TDocumentInputResponse } from "@/types";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import { isCPFValid } from "@/utils/FormValidators";

// `initial`: valor de partida quando não há sessão (cadastro, ao voltar do passo 2)
const props = defineProps<{ initial?: { document?: string; foreigner?: boolean } | null }>();
const emit = defineEmits<{ valueChanged: [response: TDocumentInputResponse] }>();
const { session: userSession } = storeToRefs(useSessionStore());

const foreignDocument = ref(false);
const documentNumber = ref("");
// como o $dirty do vuelidate: o erro só aparece depois que o usuário mexe no campo
const dirty = ref(false);

const error = computed(() => documentError(documentNumber.value, foreignDocument.value));
const errorMessage = computed(() => error.value || "Informe um documento válido");

// formato de campo que o TextInput espera ($model/$error/$errors)
const documentModel = reactive({
  get $model() {
    return documentNumber.value;
  },
  set $model(v: string) {
    documentNumber.value = v;
    dirty.value = true;
  },
  get $error() {
    return dirty.value && !!error.value;
  },
  get $errors() {
    return error.value ? [{ $message: error.value }] : [];
  },
});

function setupDocument() {
  if (props.initial?.document) {
    foreignDocument.value = !!props.initial.foreigner;
    documentNumber.value = foreignDocument.value ? props.initial.document : formatCpf(props.initial.document);
    return;
  }
  const doc = userSession.value?.document_number || "";
  foreignDocument.value = !isCPFValid(doc) ? true : userSession.value?.foreigner || false;
  // quem mora no Brasil vê o CPF com máscara (000.000.000-00)
  documentNumber.value = foreignDocument.value ? doc : formatCpf(doc);
}
setupDocument();
EventBus.$on(EventBusNames.ACCOUNT_UPDATED, setupDocument);
onBeforeUnmount(() => EventBus.$off(EventBusNames.ACCOUNT_UPDATED, setupDocument));

// trocar "Resido fora do Brasil" troca a máscara: CPF formatado ↔ texto livre
watch(foreignDocument, (foreign) => {
  documentNumber.value = foreign ? documentValue(documentNumber.value, false) : formatCpf(documentNumber.value);
});

// avisa o pai a cada mudança (e já no início, como o watch do v$ legado)
watch(
  [documentNumber, foreignDocument],
  () =>
    emit("valueChanged", {
      // a API continua recebendo o CPF só com números
      values: { document: documentValue(documentNumber.value, foreignDocument.value), foreigner: foreignDocument.value },
      valid: !error.value,
    }),
  { immediate: true },
);
</script>
